<template>
    <div class="edit_status_overlay cell_info_detail_overlay" ref="cell_overlay" :style="styleString" v-show="isShow">
        <div class="cell_info_detail_wrap" ref="cell_menu_scroll">
            <Item v-for="(d, index) in rangeData" :key="index" :value="d"
                  @updateItem="action_Update"
                  @removeItem="action_Remove"/>
        </div>
        <button @click="action_ShowReservationBlockPopup()">
            <v-icon small>mdi-cancel</v-icon>
            <span class="txt">{{ $t('reservationwindow.reservationmonitor.action_cell_add_booking_block') }}</span>
        </button>
        <button @click="action_ShowColorEditPopup(null)">
            <v-icon small>mdi-water</v-icon>
            <span class="txt">{{ $t('reservationwindow.reservationmonitor.action_cell_add_color') }}</span>
        </button>
    </div>
</template>

<script>
import { deepCopy } from '@/util/deep-copy'
import moment from '@/util/MPMoment'
import { sheetState } from '../../../sheet-state'
import { hideCellMenu, sheetOverlays, showBlockRepeatOptionPopup, showColorEditPopup, showReservationBlockPopup } from '../../../sheet-overlays'
import { sheetAxis } from '../../../sheet-axis'
import { handleRefresh } from '../../../sheet-refresh'
import Item from './Items/CellMenuItem.vue'
import { useV2ReservationCellRemoveMutation } from '@/composables/queries/v2reservationcell/use-v2-reservationcell-remove-mutation'
import { useV2ReservationBlockRemoveMutation } from '@/composables/queries/v2reservationblock/use-v2-reservationblock-remove-mutation'

export default {
    setup() {
        const { mutateAsync: removeReservationCell } = useV2ReservationCellRemoveMutation()
        const { mutateAsync: removeReservationBlock } = useV2ReservationBlockRemoveMutation()
        return { removeReservationCell, removeReservationBlock }
    },
    components: {Item},
    data() {
        return {
            sheetState,
            sheetOverlays,
            sheetAxis,
            position: {top: 0, left: 0}
        }
    },
    watch: {
        isShow: {
            deep: true,
            handler: function (newValue) {
                if (!newValue) {
                    this.scrollTop()
                    this.unregisterAlert()
                    this.removeEventListener()
                } else {
                    this.registerAlert()
                    this.addEventListener()
                    this.$nextTick(() => {
                        this.updateFrame()
                    })
                }
            }
        },
    },
    computed: {
        isShow: function () {
            return this.sheetOverlays.isShowCellMenu
        },
        mousePosition: function () {
            return this.sheetOverlays.cellMenuPosition ?? {x: 0, y: 0}
        },
        styleString: function () {
            return 'top: ' + this.position.top + 'px;' + ' left: ' + this.position.left + 'px;'
        },
        rangeData: function () {
            return this.sheetOverlays.selectedRangeCells ?? []
        },
        selectedDate: function () {
            return this.sheetAxis.selectedDate
        }
    },
    methods: {
        // MARK: - Private
        updateFrame() {
            let target = this.$refs?.cell_overlay
            if (!target) {
                this.position = {top: this.mousePosition.y, left: this.mousePosition.x}
                return
            }
            let scrollRect = this.sheetState.sheetScrollElement

            let top = (this.mousePosition.y + target.offsetHeight > scrollRect.scrollHeight) ?
                (this.mousePosition.y - target.offsetHeight) :
                this.mousePosition.y
            let left = (this.mousePosition.x + target.offsetWidth > scrollRect.scrollWidth) ?
                (this.mousePosition.x - target.offsetWidth) :
                this.mousePosition.x

            this.position = {top, left}
        },
        scrollTop() {
            let el = this.$refs['cell_menu_scroll']
            if (el == undefined) return
            el.scrollTop = 0
        },
        registerAlert() {
            this.AlertController.registAlert(this, 'ReservationBlockRemoveAlert', {
                usingAlertTag: true,
                text: this.$t('popup_reservation_block.sub_title_delete'),
                lHandler: this.alertLeftHandler,
                rHandler: this.alertRightHandler,
            })
        },
        unregisterAlert() {
            this.AlertController.unregistAlert(this, 'ReservationBlockRemoveAlert')
        },
        showBlockRepeatOptionPopupPromise(diffRepeatOption) {
            return new Promise((resolve) => {
                showBlockRepeatOptionPopup(diffRepeatOption, (type) => {
                    let response = {editRange: type}
                    resolve(response)
                })
            })
        },
        removeSuccessCallback(id) {
            handleRefresh()
            if (this.sheetOverlays.isShowCellMenu) {
                if (this.rangeData.length == 1) {
                    hideCellMenu()
                } else {
                    let newObj = deepCopy(this.rangeData)
                    this.sheetOverlays.selectedRangeCells = newObj.filter(c => c._id != id)
                }
            }
        },
        hideCurrentMenu() {
            this.sheetOverlays.isShowCellMenu = false
        },
        // MARK: - Action
        action_Update(data) {
            if (data.cellType == 'color') {
                this.action_ShowColorEditPopup(data)
            } else {
                this.action_ShowReservationBlockPopup(data)
            }
        },
        async action_Remove(data) {
            if (data.cellType == 'color') {
                await this.requestRemoveReservationColor(data)
            } else {
                let response = {}
                this.hideCurrentMenu()
                if (data.isRoutine) {
                    response = await this.showBlockRepeatOptionPopupPromise(false)
                } else {
                    let result = await this.AlertController.showAlert(this, 'ReservationBlockRemoveAlert')
                    if (result != 'right') return
                }
                await this.requestRemoveReservationBlock(data, response)
            }
        },
        action_ShowReservationBlockPopup(data) {
            this.hideCurrentMenu()
            showReservationBlockPopup(data)
        },
        action_ShowColorEditPopup(data) {
            this.hideCurrentMenu()
            showColorEditPopup(data)
        },
        // MARK : - Event
        addEventListener() {
            window.addEventListener('keyup', this.handleKeyUp)
        },
        removeEventListener() {
            window.removeEventListener('keyup', this.handleKeyUp)
        },
        handleKeyUp(e) {
            if (e.keyCode == 27) {
                if (this.sheetOverlays.isShowCellMenu) {
                    if (this.sheetOverlays.isShowColorEditPopup || this.sheetOverlays.isShowReservationBlockPopup) {
                        this.sheetOverlays.isShowCellMenu = false
                        return
                    }
                    hideCellMenu()
                }
            }
        },
        // MARK: - Network
        async requestRemoveReservationColor(data) {
            try {
                // 봉투 에러(code != 0)는 ApiError로 throw — 아래 catch에서 로그 (기존 동작 동일)
                await this.removeReservationCell({ _id: data._id })
                this.removeSuccessCallback(data._id)
            } catch (err) {
                console.log('Error processing reservation color removing: ', err)
            }
        },
        async requestRemoveReservationBlock(data, response) {
            try {
                // 봉투 에러(code != 0)는 ApiError로 throw — 아래 catch에서 로그 (기존 동작 동일)
                await this.removeReservationBlock({
                    ...response,
                    _id: data._id,
                    date: moment(this.selectedDate).format('YYYY-MM-DD')
                })
                this.removeSuccessCallback(data._id)
            } catch (err) {
                console.log('Error processing reservation block removing: ', err)
            }
        },
    }
}
</script>
